import { Button, Fade, Grid, IconButton, Modal } from '@material-ui/core'
import { AddCircleOutline, Close } from '@material-ui/icons'
import { useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { createProgram, updateProgram } from '../../actions/programActions'
import machineList from '../../data/machines'
import { useInput } from '../../hooks/useInput'
import AutoCompleteSelect from '../Form/AutoCompleteSelect'
import FileUploadTable from '../Form/FileUploadTable'
import FormContainer from '../Form/FormContainer'
import MultiFilesInput from '../Form/MultiFilesInput'
import NumberInput from '../Form/NumberInput'
import SelectInput from '../Form/SelectInput'
import TextAreaInput from '../Form/TextAreaInput'
import TextInput from '../Form/TextInput'
import Loader from '../Loader/Loader'
import Message from '../Message/Message'

interface Props {
  setShowAddProgram: (val: boolean) => void
  programToEdit: Program | undefined
  setProgramToEdit: (val: Program | undefined) => void
}

const AddEditProgram = ({
  setShowAddProgram,
  programToEdit,
  setProgramToEdit,
}: Props) => {
  const dispatch = useDispatch()

  const {
    value: name,
    bind: bindName,
    error: nameError,
    setError: setNameError,
    setValue: setName,
  } = useInput('')
  const {
    value: madeByHexagon,
    bind: bindMadeByHexagon,
    error: madeByHexagonError,
    setError: setMadeByHexagonError,
    setValue: setMadeByHexagon,
  } = useInput('')
  const {
    value: machineModel,
    error: machineModelError,
    setError: setMachineModelError,
    setValue: setMachineModel,
  } = useInput('')
  const {
    value: fixtureRequired,
    bind: bindFixtureRequired,
    error: fixtureRequiredError,
    setError: setFixtureRequiredError,
    setValue: setFixtureRequired,
  } = useInput('')
  const {
    value: fixtureType,
    bind: bindFixtureType,
    error: fixtureTypeError,
    setError: setFixtureTypeError,
    setValue: setFixtureType,
  } = useInput('')
  const {
    value: otherModelInfo,
    bind: bindOtherModelInfo,
    error: otherModelInfoError,
    setValue: setOtherModelInfo,
  } = useInput('')
  const [hours, setHours] = useState<number | undefined>(undefined)
  const [minutes, setMinutes] = useState<number | undefined>(undefined)
  const [runtimeError, setRuntimeError] = useState('')
  const [programFiles, setProgramFiles] = useState<IFile[]>([])
  const [programFilesError, setProgramFilesError] = useState('')
  const [programFilesUploading, setProgramFilesUploading] = useState([])
  const [showAddPartProgramLink, setShowAddPartProgramLink] = useState(false)
  const {
    value: partProgramLink,
    bind: bindPartProgramLink,
    setValue: setPartProgramLink,
  } = useInput('')
  const [additionalFiles, setAdditionalFiles] = useState<IFile[]>([])
  const [additionalFilesUploading, setAdditionalFilesUploading] = useState([])
  const [additionalFilesError, setAdditionalFilesError] = useState('')
  const [showPRGModal, setShowPRGModal] = useState(false)
  const [missingPRGIsOK, setMissingPRGIsOK] = useState(false)

  const {
    error: programCreateError,
    success: programCreateSuccess,
    loading: programCreateLoading,
  } = useSelector((state: RootStateOrAny) => state.programCreate)
  const {
    error: progamUpdateError,
    success: programUpdateSuccess,
    loading: programUpdateLoading,
  } = useSelector((state: RootStateOrAny) => state.programUpdate)

  const { userInfo } = useSelector((state: RootStateOrAny) => state.userLogin)
  const { project } = useSelector(
    (state: RootStateOrAny) => state.projectDetails
  )

  useEffect(() => {
    if (programCreateSuccess || programUpdateSuccess) {
      setShowAddProgram(false)
      setShowPRGModal(false)
    }
  }, [programCreateSuccess, setShowAddProgram, programUpdateSuccess])

  useEffect(() => {
    if (!!programToEdit) {
      setName(programToEdit.name)
      setMachineModel(programToEdit.machineModel)
      setFixtureType(programToEdit.fixtureType)
      setOtherModelInfo(programToEdit.otherModelInfo)
      setHours(programToEdit.runtime.hours)
      setMinutes(programToEdit.runtime.minutes)
      setProgramFiles(programToEdit.programFiles)
      setPartProgramLink(programToEdit.partProgramLink)
      setAdditionalFiles(programToEdit.additionalFiles)
      setFixtureRequired(
        programToEdit.fixtureRequired !== undefined
          ? programToEdit.fixtureRequired === true
            ? 'Yes'
            : 'No'
          : ''
      )
      setMadeByHexagon(
        programToEdit.madeByHexagon !== undefined
          ? programToEdit.madeByHexagon === true
            ? 'Yes'
            : 'No'
          : ''
      )
    }
  }, [
    programToEdit,
    setMachineModel,
    setName,
    setFixtureType,
    setOtherModelInfo,
    setHours,
    setMinutes,
    setProgramFiles,
    setPartProgramLink,
    setMadeByHexagon,
    setFixtureRequired,
    setAdditionalFiles,
  ])

  const formIsReady = () => {
    let ready = true

    if (name === '') {
      ready = false
      setNameError('Please select a project name.')
    } else {
      setNameError('')
    }

    if (madeByHexagon === '') {
      ready = false
      setMadeByHexagonError('Please make a selection.')
    } else {
      setMadeByHexagonError('')
    }

    if (!hours || !minutes) {
      ready = false
      setRuntimeError('Please make a selection.')
    } else {
      setRuntimeError('')
    }

    if (machineModel === '') {
      ready = false
      setMachineModelError('Please make a selection.')
    } else {
      setMachineModelError('')
    }

    if (fixtureRequired === '') {
      ready = false
      setFixtureRequiredError('Please make a selection.')
    } else if (fixtureRequired === 'No') {
      setFixtureType('')
    } else {
      setFixtureRequiredError('')
    }

    if (fixtureType === '' && fixtureRequired === 'Yes') {
      ready = false
      setFixtureTypeError('Please make a selection.')
    } else {
      setFixtureTypeError('')
    }

    if (programFiles.length === 0 && partProgramLink === '') {
      ready = false
      setProgramFilesError(
        'Please upload a part program or CAD file or enter a part program link'
      )
    } else {
      setProgramFilesError('')
    }

    return ready
  }

  const programHasPRG = () => {
    let hasPrg = programFiles.filter((file: IFile) =>
      file.name.toLowerCase().includes('.prg')
    )

    return hasPrg.length
  }

  const handleFormSubmit = () => {
    if (formIsReady()) {
      if (programHasPRG() || missingPRGIsOK) {
        createNewProgram()
      } else {
        setShowPRGModal(true)
      }
    }
  }

  const handleProceedClick = () => {
    setShowPRGModal(false)
    setMissingPRGIsOK(true)
    createNewProgram()
  }

  const handleProceedUpdateClick = () => {
    setMissingPRGIsOK(true)
    saveUpdatedProgram()
  }

  const createNewProgram = () => {
    let program = {
      name,
      madeByHexagon: madeByHexagon === 'Yes' ? true : false,
      runtime: { hours, minutes },
      machineModel,
      otherModelInfo,
      fixtureRequired: fixtureRequired === 'Yes' ? true : false,
      fixtureType,
      programFiles,
      additionalFiles,
      showAddPartProgramLink,
      user: userInfo.isAdmin ? project.user : userInfo._id,
    }
    dispatch(createProgram(program))
  }

  const handleUpdateProgram = () => {
    if (formIsReady()) {
      if (programHasPRG() || missingPRGIsOK) {
        saveUpdatedProgram()
      } else {
        setShowPRGModal(true)
      }
    }
  }

  const saveUpdatedProgram = () => {
    let updatedProgram = { ...programToEdit }

    if (programToEdit) {
      // @ts-ignore-next-line
      updatedProgram.name = name
      updatedProgram.madeByHexagon = madeByHexagon === 'Yes' ? true : false
      // @ts-ignore-next-line
      updatedProgram.runtime = { hours, minutes }
      // @ts-ignore-next-line
      updatedProgram.machineModel = machineModel
      // @ts-ignore-next-line
      updatedProgram.otherModelInfo = otherModelInfo
      updatedProgram.fixtureRequired = fixtureRequired === 'Yes' ? true : false
      // @ts-ignore-next-line
      updatedProgram.fixtureType = fixtureType
      updatedProgram.programFiles = programFiles
      // @ts-ignore-next-line
      updatedProgram.partProgramLink = partProgramLink
      updatedProgram.additionalFiles = additionalFiles

      dispatch(updateProgram(updatedProgram))
    }
  }

  const handleCancelClick = () => {
    setProgramToEdit(undefined)
    setShowAddProgram(false)
  }

  return (
    <FormContainer>
      <h4 className="m-b-30">
        {!programToEdit ? 'Add' : 'Edit'} program{' '}
        {userInfo.isAdmin && project.userName && `for ${project.userName}`}
      </h4>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextInput
            name="name"
            label="Program name"
            bindValue={bindName}
            error={nameError}
          />
        </Grid>

        <Grid item xs={6} className="p-t-0">
          <SelectInput
            label="Did Hexagon make this program?"
            name="make"
            options={[
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
            ]}
            bindValue={bindMadeByHexagon}
            error={madeByHexagonError}
          />
        </Grid>

        <Grid item xs={6} className="p-t-0">
          <div className={runtimeError ? 'error' : ''}>
            <label htmlFor="size">What is the runtime of this program?</label>
          </div>
          <Grid container spacing={2}>
            <Grid item xs={6} className="p-b-0">
              <NumberInput
                value={hours}
                setValue={setHours}
                error={runtimeError}
                label="HR"
              />
            </Grid>
            <Grid item xs={6} className="p-b-0">
              <NumberInput
                value={minutes}
                setValue={setMinutes}
                label="MIN"
                error={runtimeError}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={6} className="p-t-0">
          <AutoCompleteSelect
            label="Which machine model is your program for?"
            name="machine"
            options={machineList}
            value={machineModel as string}
            setValue={setMachineModel}
            minWidth={340}
            error={machineModelError}
          />
        </Grid>

        <Grid item xs={6} className="p-t-0">
          {machineModel === 'Other' && (
            <TextInput
              name="otherModelInfo"
              label="Model name and info"
              bindValue={bindOtherModelInfo}
              error={otherModelInfoError}
            />
          )}
        </Grid>

        <Grid item xs={6} className="p-t-0">
          <SelectInput
            label="Is fixture required?"
            name="fixtureRequired"
            options={[
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
            ]}
            bindValue={bindFixtureRequired}
            error={fixtureRequiredError}
          />
        </Grid>

        <Grid item xs={6} className="p-t-0">
          <SelectInput
            label="Fixture type"
            name="fixtureType"
            options={[
              { value: 'Custom', label: 'Custom' },
              { value: 'Modular', label: 'Modular' },
              { value: 'Other', label: 'Other' },
            ]}
            bindValue={bindFixtureType}
            error={fixtureTypeError}
            disabled={fixtureRequired === 'No'}
          />
        </Grid>

        <Grid item sm={12} className="p-t-0 p-b-0">
          <MultiFilesInput
            disabled={false}
            name="programFiles"
            label="Program files"
            filesUploading={programFilesUploading}
            setFiles={setProgramFiles}
            setFilesUploading={setProgramFilesUploading}
            contentTop={
              <>
                Drag and drop or click to add
                <strong> Program files</strong>
              </>
            }
            contentBottom="The .prg is required. Please also include your probe configuration .prb and references files .cad if possible"
            error={programFilesError}
            setError={setProgramFilesError}
          />

          {(programFiles.length > 0 || programFilesUploading.length > 0) && (
            <FileUploadTable
              data={programFiles}
              setData={setProgramFiles}
              filesUploading={programFilesUploading}
            />
          )}

          {showAddPartProgramLink ? (
            <TextAreaInput
              name="partProgramLink"
              label="If your program files cannot be uploaded above, you can provide a URL and any instructions here for us to download the files."
              bindValue={bindPartProgramLink}
              rows={4}
              className="m-b-16"
            />
          ) : (
            <p
              className="link m-b-16"
              onClick={() => setShowAddPartProgramLink(true)}
            >
              <AddCircleOutline className="m-r-5" />
              Need to send a link instead?
            </p>
          )}
        </Grid>
        <Grid item xs={12} className="p-t-0 p-b-0">
          <MultiFilesInput
            disabled={false}
            name="additionalFiled"
            label="Additional files (optional)"
            filesUploading={additionalFilesUploading}
            setFiles={setAdditionalFiles}
            setFilesUploading={setAdditionalFilesUploading}
            error={additionalFilesError}
            setError={setAdditionalFilesError}
            contentTop={
              <>
                Drag and drop or click to add
                <strong> additional files</strong>
              </>
            }
            contentBottom=".rtp, power points, or pdf etc. can go here."
          />
          {(additionalFiles.length > 0 ||
            additionalFilesUploading.length > 0) && (
            <div className="m-t-32">
              <FileUploadTable
                data={additionalFiles}
                setData={setAdditionalFiles}
                filesUploading={additionalFilesUploading}
              />
            </div>
          )}
        </Grid>
      </Grid>

      <div className="m-t-40 flex flex-end">
        <Button
          variant="outlined"
          color="primary"
          onClick={handleCancelClick}
          disabled={programUpdateLoading || programCreateLoading}
        >
          Cancel
        </Button>

        {!programToEdit ? (
          <Button
            variant="contained"
            color="primary"
            style={{ marginLeft: 20 }}
            onClick={handleFormSubmit}
            disabled={programUpdateLoading || programCreateLoading}
          >
            Add program
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            style={{ marginLeft: 20 }}
            onClick={handleUpdateProgram}
            disabled={programUpdateLoading || programCreateLoading}
          >
            Update program
          </Button>
        )}
      </div>

      {programCreateError && (
        <Message variant="error">{programCreateError}</Message>
      )}

      {progamUpdateError && (
        <Message variant="error">{progamUpdateError}</Message>
      )}

      <Modal open={showPRGModal} onClose={() => setShowPRGModal(false)}>
        <Fade in={showPRGModal}>
          <div className="modal-window">
            <div className="modal-header">
              <div>Missing file</div>
              <IconButton onClick={() => setShowPRGModal(false)}>
                <Close />
              </IconButton>
            </div>
            <div className="modal-body">
              <h4 className="m-b-32">Missing file</h4>
              <p>
                A .prg file has not been uploaded to program files. This may
                effect our ability to provide an accurate cost estimate. Would
                you like to proceed anyway?
              </p>
              <div className="actions m-t-40 flex-end flex">
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => setShowPRGModal(false)}
                  disabled={programUpdateLoading || programCreateLoading}
                  className={`m-r-24 ${
                    programUpdateLoading || programCreateLoading
                      ? 'loading'
                      : ''
                  }`}
                >
                  Return and upload file
                </Button>

                {!programToEdit ? (
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleProceedClick}
                    disabled={programUpdateLoading || programCreateLoading}
                    className={
                      programUpdateLoading || programCreateLoading
                        ? 'loading'
                        : ''
                    }
                  >
                    {programUpdateLoading || programCreateLoading ? (
                      <Loader />
                    ) : (
                      'Yes, proceed to the next page'
                    )}
                  </Button>
                ) : (
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleProceedUpdateClick}
                    disabled={programUpdateLoading || programCreateLoading}
                    className={
                      programUpdateLoading || programCreateLoading
                        ? 'loading'
                        : ''
                    }
                  >
                    {programUpdateLoading || programCreateLoading ? (
                      <Loader />
                    ) : (
                      'Yes, proceed to the next page'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Fade>
      </Modal>
    </FormContainer>
  )
}

export default AddEditProgram
